import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { organizationAPI } from '../services/api';
import { useAuth } from './AuthContext';

const OrganizationContext = createContext();

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within OrganizationProvider');
  }
  return context;
};

const STORAGE_PREFIX = 'ospito_organization_cache';

const DEFAULT_THEME = {
  primary_color: '#14b8a6',
  secondary_color: '#0d9488',
  accent_color: '#0891b2',
  sidebar_color: '#155e75',
};

// Convert hex to HSL components for Tailwind-compatible CSS vars
const hexToHSL = (hex) => {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  }
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: break;
    }
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
};

// Derive lighter/darker shades from a base color for gradient usage
const adjustLightness = (hex, amount) => {
  const { h, s, l } = hexToHSL(hex);
  const newL = Math.max(0, Math.min(100, l + amount));
  return `hsl(${h}, ${s}%, ${newL}%)`;
};

// Apply theme colors as CSS custom properties on documentElement
const applyThemeToDOM = (colors) => {
  const root = document.documentElement;
  const c = { ...DEFAULT_THEME, ...colors };
  root.style.setProperty('--theme-primary', c.primary_color);
  root.style.setProperty('--theme-secondary', c.secondary_color);
  root.style.setProperty('--theme-accent', c.accent_color);
  root.style.setProperty('--theme-sidebar', c.sidebar_color);
  // Lighter/darker variants for gradients & hover
  root.style.setProperty('--theme-primary-light', adjustLightness(c.primary_color, 10));
  root.style.setProperty('--theme-primary-dark', adjustLightness(c.primary_color, -8));
  root.style.setProperty('--theme-secondary-dark', adjustLightness(c.secondary_color, -8));
  root.style.setProperty('--theme-accent-light', adjustLightness(c.accent_color, 15));
  // Semi-transparent variants
  root.style.setProperty('--theme-primary-10', c.primary_color + '1a');
  root.style.setProperty('--theme-primary-20', c.primary_color + '33');
  root.style.setProperty('--theme-accent-20', c.accent_color + '33');
};

// Get cached organization data from localStorage for instant loading
const getCachedOrganization = (tenantId) => {
  try {
    const key = tenantId ? `${STORAGE_PREFIX}_${tenantId}` : STORAGE_PREFIX;
    const cached = localStorage.getItem(key);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.warn('Failed to read organization cache:', e);
  }
  return null;
};

// Save organization data to localStorage for instant loading on next visit
const cacheOrganization = (org, tenantId) => {
  try {
    const key = tenantId ? `${STORAGE_PREFIX}_${tenantId}` : STORAGE_PREFIX;
    localStorage.setItem(key, JSON.stringify(org));
  } catch (e) {
    console.warn('Failed to cache organization:', e);
  }
};

// One-time cleanup: remove the old unscoped cache key to prevent cross-tenant leakage
try {
  const oldKey = STORAGE_PREFIX; // 'ospito_organization_cache' without tenant suffix
  if (localStorage.getItem(oldKey)) {
    localStorage.removeItem(oldKey);
  }
} catch (e) { /* ignore */ }

export const OrganizationProvider = ({ children }) => {
  const { user } = useAuth();
  const tenantId = user?.tenant_id || null;
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const hasCachedData = useRef(false);
  // Track the tenant we last fetched for, so we re-fetch on tenant change
  const lastFetchedTenantRef = useRef(null);

  const fetchOrganization = useCallback(async (fetchTenantId) => {
    try {
      // Only show loading if we don't have cached data for this tenant
      const cached = getCachedOrganization(fetchTenantId);
      if (!cached) {
        setLoading(true);
      }
      const response = await organizationAPI.getSettings();
      if (response.data) {
        const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace('localhost', window.location.hostname);
        const themeColors = {
          primary_color: response.data.primary_color || DEFAULT_THEME.primary_color,
          secondary_color: response.data.secondary_color || DEFAULT_THEME.secondary_color,
          accent_color: response.data.accent_color || DEFAULT_THEME.accent_color,
          sidebar_color: response.data.sidebar_color || DEFAULT_THEME.sidebar_color,
        };
        applyThemeToDOM(themeColors);
        const orgData = {
          name: response.data.name || '',
          name_line1: response.data.name_line1 || '',
          name_line1_size: response.data.name_line1_size || 14,
          name_line2: response.data.name_line2 || '',
          name_line2_size: response.data.name_line2_size || 12,
          facility_type: response.data.facility_type || '',
          address: response.data.address || '',
          address_line_2: response.data.address_line_2 || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          website: response.data.website || '',
          timezone: response.data.timezone || 'Africa/Nairobi',
          time_format: response.data.time_format || '24h',
          registration_number: response.data.registration_number || '',
          tax_id: response.data.tax_id || '',
          logo_url: response.data.logo_url ? `${API_URL}${response.data.logo_url}` : null,
          stamp_url: response.data.stamp_url ? `${API_URL}${response.data.stamp_url}` : null,
          ...themeColors,
        };
        setOrganization(orgData);
        // Cache for instant loading on next visit (scoped by tenant)
        cacheOrganization(orgData, fetchTenantId);
        hasCachedData.current = true;
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error fetching organization:', error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when tenant changes or on manual refresh
  useEffect(() => {
    if (!tenantId) {
      // No user logged in — reset to defaults
      setOrganization(null);
      applyThemeToDOM(DEFAULT_THEME);
      setLoading(false);
      lastFetchedTenantRef.current = null;
      return;
    }
    // If tenant changed, load from cache first for instant display, then fetch
    if (lastFetchedTenantRef.current !== tenantId) {
      const cached = getCachedOrganization(tenantId);
      if (cached) {
        setOrganization(cached);
        applyThemeToDOM(cached);
        hasCachedData.current = true;
      } else {
        setOrganization(null);
        applyThemeToDOM(DEFAULT_THEME);
        hasCachedData.current = false;
      }
      lastFetchedTenantRef.current = tenantId;
      fetchOrganization(tenantId);
    }
  }, [tenantId, fetchOrganization]);

  // Separate effect for manual refresh (same tenant)
  useEffect(() => {
    if (refreshTrigger > 0 && tenantId) {
      fetchOrganization(tenantId);
    }
  }, [refreshTrigger, tenantId, fetchOrganization]);

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <OrganizationContext.Provider value={{ refreshTrigger, triggerRefresh, organization, loading, refetchOrganization: fetchOrganization, applyThemeToDOM }}>
      {children}
    </OrganizationContext.Provider>
  );
};
